import { useNavigation } from "@react-navigation/native";
import { Formik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { ONBOARDING_NAVIGATION_ERROR } from "../../constants/onboardingMessages";
import ScreenNames from "../../constants/routeNames";
import { getNextScreen } from "../../navigation/simpleOnboardingFlow";
import type { SimpleDietEnum } from "../../services/backendTypes";
import { selectDiet, setDiet } from "../../slices/simpleOnboardingSlice";
import SelectionScreen, { type Option } from "../New_DIY_form/SelectionScreen";

const validationSchema = Yup.object().shape({
  diet: Yup.string().oneOf(["none", "VEGETARIAN", "VEGAN", "PESCATARIAN"]).required("Please select a diet option"),
});

const SimpleOnboardingDietScreen = (): JSX.Element => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const dispatch = useDispatch();

  const selectedDiet = useSelector(selectDiet);

  const options: Option[] = [
    {
      value: "none",
      label: t("simple_onboarding.diet.none"),
      description: "",
      icon: "Utensils",
      iconType: "lucide",
    },
    {
      value: "VEGETARIAN" as SimpleDietEnum,
      label: t("simple_onboarding.diet.VEGETARIAN"),
      description: "",
      icon: "Leaf",
      iconType: "lucide",
    },
    {
      value: "VEGAN" as SimpleDietEnum,
      label: t("simple_onboarding.diet.VEGAN"),
      description: "",
      icon: "Vegan",
      iconType: "lucide",
    },
    {
      value: "PESCATARIAN" as SimpleDietEnum,
      label: t("simple_onboarding.diet.PESCATARIAN"),
      description: "",
      icon: "Fish",
      iconType: "lucide",
    },
  ];

  return (
    <Formik
      initialValues={{ diet: selectedDiet || "none" }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const dietToSave = values.diet === "none" ? null : (values.diet as SimpleDietEnum);
        dispatch(setDiet(dietToSave));
        const nextScreen = getNextScreen(ScreenNames.SimpleOnboarding_3_Diet);
        if (nextScreen) {
          navigation.navigate(nextScreen as never);
        } else {
          alert(ONBOARDING_NAVIGATION_ERROR);
        }
      }}
    >
      {({ values, setFieldValue, errors, handleSubmit }) => (
        <SelectionScreen
          title={t("simple_onboarding.diet.title")}
          subtitle=""
          options={options}
          multipleSelection={false}
          selectedOptions={values.diet ? [values.diet] : []}
          setSelectedOptions={(dietOptions) => setFieldValue("diet", dietOptions[0])}
          onPressNext={handleSubmit}
          onPressBack={() => navigation.goBack()}
          nextButtonDisabled={!values.diet}
          errorMessage={errors.diet}
          nextButtonTestIdPostfix="diet"
        />
      )}
    </Formik>
  );
};

export default SimpleOnboardingDietScreen;
